import { useRef, useState } from "react";
import { confirmDevServer, type ProjectDirectory } from "@/api/spaces";
import { Button, PocketSection } from "@/components/ui";
import { useT } from "@/i18n";
import { settingsProjectsMessages } from "@/i18n/messages/settings-projects";
import { SettingsFieldError, SettingsTextField } from "./settings-text-field";
import {
  projectError,
  ProjectPageUnavailable,
  useProjectPage,
} from "./project-page-state";
import {
  useDirectoryEditor,
  useDirectoryText,
} from "./project-directory-fields";

export function DevServerPage({
  projectId,
  folderId,
}: {
  projectId: string;
  folderId: string;
}) {
  const state = useProjectPage(projectId, folderId);
  if (state.loading || !state.project || !state.folder)
    return (
      <ProjectPageUnavailable loading={state.loading} retry={state.retry} />
    );
  return (
    <DevServerEditor
      key={`${projectId}:${folderId}`}
      projectId={projectId}
      initial={state.folder}
    />
  );
}
function DevServerEditor({
  projectId,
  initial,
}: {
  projectId: string;
  initial: ProjectDirectory;
}) {
  const t = useT(settingsProjectsMessages);
  const editor = useDirectoryEditor(projectId, initial);
  const prepare = useDirectoryText(projectId, editor, "dev_server_prepare");
  const command = useDirectoryText(projectId, editor, "dev_server_command");
  const url = useDirectoryText(projectId, editor, "dev_server_url");
  const ready = useDirectoryText(projectId, editor, "dev_server_ready_check");
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const writing = useRef(false);
  const dirty =
    prepare.text !== editor.directory.dev_server_prepare ||
    command.text !== editor.directory.dev_server_command ||
    url.text !== editor.directory.dev_server_url ||
    ready.text !== editor.directory.dev_server_ready_check;
  const confirmed = Boolean(editor.directory.dev_server_confirmed_at) && !dirty;
  async function confirm() {
    if (writing.current) return;
    writing.current = true;
    setConfirming(true);
    setError(null);
    try {
      // Serialize pending edits before confirming; the server invalidates the
      // confirmation whenever any dev-server setting changes.
      for (const field of [prepare, command, url, ready])
        if (!(await field.saveNow())) return;
      editor.setDirectory(await confirmDevServer(projectId, initial.id));
    } catch (failure) {
      setError(projectError(failure, t("confirmDevServerError")));
    } finally {
      writing.current = false;
      setConfirming(false);
    }
  }
  return (
    <div data-testid="settings-page-dev-server">
      <PocketSection testId="dev-server-settings">
        <SettingsTextField
          {...prepare.inputProps}
          disabled={confirming}
          error={prepare.error}
          label={t("prepareCommand")}
          mono
          testId="dev-prepare"
          // i18n-ignore: an example command
          placeholder="bun install"
        />
        <SettingsTextField
          {...command.inputProps}
          disabled={confirming}
          error={command.error}
          label={t("runCommand")}
          mono
          testId="dev-command"
          // i18n-ignore: an example command
          placeholder="bun dev"
        />
        <SettingsTextField
          {...url.inputProps}
          disabled={confirming}
          error={url.error}
          label="URL"
          mono
          testId="dev-url"
          // i18n-ignore: an example URL
          placeholder="http://localhost:5173"
        />
        <SettingsTextField
          {...ready.inputProps}
          disabled={confirming}
          error={ready.error}
          label={t("readyCheck")}
          mono
          testId="dev-ready"
          placeholder={t("readyCheckPlaceholder")}
        />
        <p className="px-2 py-2 text-appkit-small text-muted">
          {t("confirmHint")}
        </p>
        <Button
          size="sm"
          variant="secondary"
          disabled={confirming || confirmed || !command.text.trim()}
          data-testid="dev-confirm"
          onClick={() => void confirm()}
        >
          {confirming
            ? t("confirming")
            : confirmed
              ? t("confirmed")
              : t("markConfirmed")}
        </Button>
        <SettingsFieldError error={error} testId="dev-error" />
      </PocketSection>
    </div>
  );
}
